import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Users } from "lucide-react";
import {
  Container,
  Section,
  Eyebrow,
  Badge,
  Card,
} from "../components/ui";
import { NewsletterForm } from "../components/NewsletterForm";
import { useSeo } from "../lib/seo";
import { getPostBySlug, listPostsSorted } from "../content/blog";
import { MarkdownPost } from "../components/MarkdownPost";

function formatDate(iso: string): string {
  const parts = iso.split("-").map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function BlogPost() {
  const { slug = "" } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug);
  // Hooks must run before any early return, so seo runs even when post is missing
  // (the seo will be overwritten by the index page once Navigate fires).
  useSeo({
    title: post
      ? `${post.title} — WeConsult Rx Blog`
      : "Article not found — WeConsult Rx Blog",
    description: post
      ? post.description
      : "The article you were looking for is no longer available.",
    path: post ? `/blog/${post.slug}` : "/blog",
    noIndex: !post,
  });
  // Unknown slug → bounce to /blog index. NotFound is only for non-blog 404s
  // (keeps the 404 page from cluttering with a "blog post not found" variant).
  if (!post) return <Navigate to="/blog" replace />;

  const all = listPostsSorted();
  const idx = all.findIndex((p) => p.slug === post.slug);
  const related = all.filter((_, i) => i !== idx).slice(0, 2);

  return (
    <>
      <Section className="!pb-8">
        <Container>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            All articles
          </Link>

          <article className="mt-8">
            <header className="max-w-3xl">
              <Eyebrow>Blog</Eyebrow>
              <div className="mt-4 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <Badge key={t} tone="forest">
                    {t}
                  </Badge>
                ))}
              </div>
              <dl
                className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-500"
                data-testid="blog-post-meta"
              >
                <div className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-forest-700" aria-hidden />
                  <dt className="sr-only">Published</dt>
                  <dd>{formatDate(post.date)}</dd>
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-forest-700" aria-hidden />
                  <dt className="sr-only">Reading time</dt>
                  <dd>{post.readingTimeMin} min read</dd>
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-forest-700" aria-hidden />
                  <dt className="sr-only">Audience</dt>
                  <dd>{post.audience}</dd>
                </div>
                <div className="text-ink-500">
                  By <span className="text-ink-700 font-medium">{post.author}</span>
                </div>
              </dl>
            </header>

            <div className="mt-8" data-testid="blog-post-body">
              <MarkdownPost source={post.body} />
            </div>
          </article>
        </Container>
      </Section>

      {related.length > 0 && (
        <Section tone="soft">
          <Container>
            <h2 className="text-2xl md:text-3xl font-bold text-ink-900">
              More from the blog
            </h2>
            <ul className="mt-8 grid gap-6 md:grid-cols-2">
              {related.map((p) => (
                <Card as="li" key={p.slug}>
                  <div className="flex flex-wrap gap-2">
                    {p.tags.slice(0, 3).map((t) => (
                      <Badge key={t} tone="forest">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <h3 className="mt-4 text-xl md:text-2xl font-bold">
                    <Link
                      to={`/blog/${p.slug}`}
                      className="text-ink-900 hover:text-forest-700 transition-colors"
                    >
                      {p.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-ink-600 leading-relaxed">
                    {p.description}
                  </p>
                </Card>
              ))}
            </ul>

            <Card className="mt-12 max-w-2xl mx-auto bg-ink-900 text-white border-transparent">
              <p className="text-sm uppercase tracking-tagline font-semibold text-forest-400">
                Subscribe
              </p>
              <h3 className="mt-3 text-xl md:text-2xl font-bold text-white">
                Get the next post in your inbox.
              </h3>
              <p className="mt-3 text-ink-300">
                Quarterly updates, no spam, unsubscribe anytime.
              </p>
              <NewsletterForm className="mt-5" />
            </Card>
          </Container>
        </Section>
      )}
    </>
  );
}
